// components/LegalRepresentatives/RemoveRepresentativeModal.jsx
import { useState, useEffect } from 'react';
import { 
  FaTimes, 
  FaExclamationTriangle, 
  FaUserShield, 
  FaEnvelope, 
  FaInfoCircle,
  FaUserCheck,
  FaUserSlash 
} from 'react-icons/fa';
import { useRemoveRepresentative } from '../service/Hooks/useRemoveRepresentative';


/**
 * Modal professionnel pour dissocier des mandataires
 * - Gère les cas individuels et "tous"
 * - Demande l'email si nécessaire (tout en une seule requête)
 * - Messages clairs et explicites
 */
const RemoveRepresentativeModal = ({
  isOpen,
  onClose,
  account,
  representative = null,
  onSuccess,
  refreshData
}) => {
  console.log('Representative object:', representative); // <-- AJOUTER CETTE LIGNE
  console.log('Representative ID:', representative?.id); // <-- ET CETTE LIGNE

  const [email, setEmail] = useState('');
  const [reason, setReason] = useState('');
  const [shouldAskForEmail, setShouldAskForEmail] = useState(false);
  
  const { removeRepresentative, isLoading } = useRemoveRepresentative();
  
  // Réinitialiser l'état quand le modal s'ouvre
  useEffect(() => {
    if (isOpen) {
      setEmail(account?.email || '');
      setReason('');
      setShouldAskForEmail(false);
      
      // Si le compte n'a pas d'email, on prépare la demande
      if (!account?.email && !representative) {
        setShouldAskForEmail(true);
      }
    }
  }, [isOpen, account, representative]);
  
  if (!isOpen) return null;
  
  const handleSubmit = async (withEmail = false) => {
    // SIMPLIFIER : utiliser directement l'ID du mandat
    const params = {
      accountId: account.id,
      representativeId: representative?.representativeAccountId, // <-- CHANGER ICI
      options: {
        newStatus: 'INACTIVE',
        forceEmailUpdate: withEmail && email.trim() ? true : false,
        newEmail: withEmail && email.trim() ? email.trim() : null,
        reason: reason.trim() || null
      }
    };
    
    // console.log('📤 API params envoyés:', params);
    
    const result = await removeRepresentative(params);
    
    if (result) {
      // console.log('✅ Succès:', result);
      if (onSuccess) onSuccess(result);
      if (refreshData) refreshData();
      handleClose();
    }
  };
  
  const handleClose = () => {
    onClose();
  };
  
  // Validation de l'email
  const isEmailValid = () => {
    if (!email.trim()) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  // Obtenir le titre contextuel
  const getTitle = () => {
    if (representative) {
      return `Dissocier ${representative.displayName}`;
    }
    return 'Dissocier tous les mandataires';
  };
  
  // Obtenir l'icône contextuelle
  const getIcon = () => {
    if (representative) return <FaUserSlash className="me-2" />;
    return <FaUserShield className="me-2" />;
  };
  
  // Obtenir le message de confirmation
  const getConfirmationMessage = () => {
    if (representative) {
      return (
        <div className="alert alert-warning">
          <FaExclamationTriangle className="me-2" />
          <strong>Confirmation requise</strong>
          <p className="mb-0 mt-2">
            Vous allez dissocier <strong className="text-primary">{representative.displayName}</strong> 
            du compte <strong>{account.displayName}</strong>.
          </p> 
          <br/>
          <ol>
            <li>
              <strong> 1. Ce compte n'a pas d'email.</strong> Si ce compte n'a qu'un seul mandataire, 
              nous conseillons d'abandonner ici et d'utiliser "Dissocier tout". Cela vous donnera 
              la possibilité de lui fournir un email, le transformant en compte non-administré 
              nécessitant un email pour les communications.
            </li>
            <li>
              <strong>2. Si ce compte a plusieurs mandataires,</strong> il est conseillé d'utiliser 
              "Dissocier tout" pour tout supprimer et lui donner un email. Sinon, il ne pourra 
              pas avoir d'email et l'action est irréversible.
            </li>
            <li>
              <strong>3. Si vous avez dissocié un à un</strong> et qu'il ne reste plus qu'un seul 
              mandataire, utilisez "Dissocier tout" pour lui passer un email. Sinon, il restera 
              sans email à jamais.
            </li>
          </ol>
        </div>
      );
    }
    
    return (
      <div className="alert alert-danger">
        <FaExclamationTriangle className="me-2" />
        <strong>Action critique</strong>
        <p className="mb-0 mt-2">
          Vous allez dissocier <strong className="text-danger">TOUS LES MANDATAIRES</strong> 
          du compte <strong>{account.displayName}</strong>.
        </p>
      </div>
    );
  };
  
  // Rendu principal (une seule étape avec option email si nécessaire)
  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content border-0 shadow-lg">
          {/* En-tête */}
          <div className="modal-header bg-gradient-primary text-white">
            <h5 className="modal-title d-flex align-items-center">
              {getIcon()}
              {getTitle()}
            </h5>
            <button 
              type="button" 
              className="btn-close btn-close-white" 
              onClick={handleClose}
              disabled={isLoading}
            ></button>
          </div>
          
          {/* Corps */}
          <div className="modal-body">
            {/* Message de confirmation */}
            {getConfirmationMessage()}
            
            {/* Informations sur le compte */}
            <div className="card border-light mb-3">
              <div className="card-body">
                <h6 className="card-title text-muted mb-2">
                  <FaInfoCircle className="me-2" />
                  Informations du compte
                </h6>
                <div className="row small">
                  <div className="col-6">
                    <strong>Email actuel :</strong>
                    <div className="text-truncate" title={account.email || 'Aucun'}>
                      {account.email || <span className="text-muted fst-italic">Aucun</span>}
                    </div>
                  </div>
                  <div className="col-6">
                    <strong>Statut :</strong>
                    <div>
                      <span className={`badge ${account.isManagedAccount ? 'bg-success' : 'bg-secondary'}`}>
                        {account.isManagedAccount ? 'Administré' : 'Non-administré'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Section email (si nécessaire) */}
            {(!account.email && !representative) && (
              <div className="mb-3">
                <div className="alert alert-info">
                  <FaInfoCircle className="me-2" />
                  <strong>Email recommandé</strong>
                  <p className="mb-0 mt-2 small">
                    Ce compte n'a pas d'email. Si vous dissociez tous les mandataires, 
                    il deviendra un compte non-administré et nécessitera un email pour les communications.
                  </p>
                </div>
                
                <label className="form-label">
                  <FaEnvelope className="me-2" />
                  Email du compte <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  className={`form-control ${email && !isEmailValid() ? 'is-invalid' : ''}`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="client@entreprise.com"
                  disabled={isLoading}
                />
                {email && !isEmailValid() && (
                  <div className="invalid-feedback">
                    Veuillez entrer un email valide
                  </div>
                )}
                <div className="form-text small text-muted">
                  Cet email permettra au client de se connecter directement.
                </div>
              </div>
            )}
            
            {/* Champ raison */}
            <div className="mb-3">
              <label className="form-label">
                Raison de la dissociation <span className="text-muted">(optionnel)</span>
              </label>
              <textarea
                className="form-control"
                rows="2"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ex: Fin de mandat, Changement de représentant..."
                disabled={isLoading}
              />
            </div>
            
            {/* Conséquences de l'action */}
            <div className="alert alert-light border">
              <h6 className="mb-2">
                <FaInfoCircle className="me-2" />
                Conséquences de cette action
              </h6>
              <ul className="mb-0 small">
                <li>Le mandat sera marqué comme "INACTIVE"</li>
                <li>Une date de fin sera enregistrée</li>
                {!representative && (
                  <>
                    <li>Tous les mandataires seront dissociés</li>
                    {!account.email && (
                      <li className="text-primary">
                        Le compte sera converti en compte non-administré
                      </li>
                    )}
                  </>
                )}
              </ul>
            </div>
          </div>
          
          {/* Pied de page */}
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-outline-secondary" 
              onClick={handleClose}
              disabled={isLoading}
            >
              <FaTimes className="me-2" />
              Annuler
            </button>
            
            {/* Boutons d'action contextuels */}
            {!representative && !account.email ? (
              // Cas "tous" sans email : deux boutons
              <>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={() => handleSubmit(false)}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Traitement...
                    </>
                  ) : (
                    <>
                      Dissocier sans email
                    </>
                  )}
                </button>
                
                <button 
                  type="button" 
                  className="btn btn-primary"
                  onClick={() => handleSubmit(true)}
                  disabled={isLoading || !isEmailValid()}
                >
                  {isLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Traitement...
                    </>
                  ) : (
                    <>
                      <FaEnvelope className="me-2" />
                      Dissocier avec email
                    </>
                  )}
                </button>
              </>
            ) : (
              // Cas normal (individuel ou "tous" avec email existant)
              <button 
                type="button" 
                className={`btn ${representative ? 'btn-warning' : 'btn-danger'}`}
                onClick={() => handleSubmit(false)}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Traitement...
                  </>
                ) : (
                  <>
                    {representative ? (
                      <>
                        <FaUserSlash className="me-2" />
                        Dissocier ce mandataire
                      </>
                    ) : (
                      <>
                        <FaUserCheck className="me-2" />
                        Dissocier tous les mandataires
                      </>
                    )}
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RemoveRepresentativeModal;